import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, Inject, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { API_MODE, TenantService } from '@vsolv/core/multi-tenant/web';
import { API_BASE_URL } from '@vsolv/dev-kit/ngx';
import { FIREBASE_AUTH, idToken$ } from '@vsolv/packages/firebase/web';
import { Payment } from '@vsolv/packages/payments/domain';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { Customer } from '@wsphere/customers/domain';
import { CustomerService } from '@wsphere/customers/web';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import { Auth } from 'firebase/auth';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';

@Component({ changeDetection: ChangeDetectionStrategy.Eager, templateUrl: './payments.page.html', standalone: false })
export class StaffPaymentsPage implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private tenantSvc: TenantService,
    private formBuilder: FormBuilder,
    @Inject(FIREBASE_AUTH) private auth: Auth,
    private securitySvc: SecurityService,
    private customerSvc: CustomerService,
    @Inject(API_MODE) private apiMode: string
  ) {}

  private apiBaseUrl = inject(API_BASE_URL);
  private syncSub?: Subscription;
  private readonly filtersReady$ = new BehaviorSubject(false);

  positions = [
    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' }),
    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
  ];

  filtersOverlayOpen = false;
  totalPayments: number | null = null;

  protected form = this.formBuilder.group({
    selectedCustomer: [null as Customer.Model | null],
    selectedDistributor: [null as Distributor.DistributorWithPermissionKeyQueryResponse | null],
    status: [this.parseStatusFromUrl()],
    warrantyId: [this.route.snapshot.queryParamMap.get('warrantyId') ?? ''],
  });

  protected customerId$: Observable<string> = this.form.valueChanges.pipe(
    debounceTime(500),
    startWith(null),
    map(() => this.form.getRawValue().selectedCustomer?.userId ?? '')
  );

  protected status$: Observable<Payment.Status[] | null> = this.form.controls.status.valueChanges.pipe(
    startWith(this.form.controls.status.value),
    map(status => status ?? null)
  );

  protected distributorId$: Observable<string> = this.form.valueChanges.pipe(
    debounceTime(500),
    startWith(null),
    map(() => this.form.getRawValue().selectedDistributor?.id ?? '')
  );

  protected warrantyId$: Observable<string> = this.form.controls.warrantyId.valueChanges.pipe(
    debouncedSearch(),
    startWith(this.form.controls.warrantyId.value ?? ''),
    map(warrantyId => warrantyId ?? '')
  );

  protected filters$ = combineLatest([
    this.filtersReady$,
    this.customerId$,
    this.status$,
    this.distributorId$,
    this.warrantyId$,
    this.securitySvc.globalDistributors$,
  ]).pipe(
    filter(([ready]) => ready),
    map(([, userId, status, distributorId, warrantyId]) => {
      return {
        ...(userId ? { userId } : {}),
        ...(status ? { status } : {}),
        ...(distributorId ? { distributorId } : {}),
        ...(warrantyId ? { referenceId: warrantyId } : {}),
      };
    })
  );

  protected exportUrl$ = combineLatest([idToken$(this.auth), this.filters$, this.securitySvc.globalDistributors$]).pipe(
    map(([idToken, filters, globalDistributors]) => {
      const permissionKeys =
        ((globalDistributors ?? []) as Distributor.PartialModel[]).map(item => item.permissionKey).join(',') || 'null';

      const params = [
        ...(filters.userId ? [`userId=${filters.userId}`] : []),
        ...(filters.status ? [`status=${filters.status?.join(',')}`] : []),
        ...(filters.referenceId ? [`referenceId=${filters.referenceId}`] : []),
        ...(filters.distributorId ? [`distributorId=${filters.distributorId}`] : []),
        `tenantId=${this.tenantSvc.tenantId}`,
        `permissionKeys=${permissionKeys}`,
        `mode=${this.apiMode}`,
        `idToken=${idToken}`,
      ].join('&');

      return `${this.apiBaseUrl}/v1/admin-dashboard/payments/csv?${params}`;
    })
  );

  async ngOnInit() {
    await this.restoreFiltersFromUrl();
    this.filtersReady$.next(true);
    this.syncSub = this.form.valueChanges.pipe(debounceTime(500)).subscribe(() => this.syncFiltersToUrl());
  }

  ngOnDestroy() {
    this.syncSub?.unsubscribe();
  }

  async rowClicked(payment: Payment.Model) {
    await this.router.navigate([payment.id], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.form.controls.selectedCustomer.value) count++;
    if (this.form.controls.selectedDistributor.value) count++;
    if (this.form.controls.status.value?.length) count++;
    return count;
  }

  toggleFiltersOverlay() {
    this.filtersOverlayOpen = !this.filtersOverlayOpen;
  }

  closeFiltersOverlay() {
    this.filtersOverlayOpen = false;
  }

  clearAllFilters() {
    this.form.patchValue({
      selectedCustomer: null,
      selectedDistributor: null,
      status: [],
    });
  }

  setTotalPayments(items?: number | null) {
    if (items === undefined || items === null) return;
    this.totalPayments = items;
  }

  private parseStatusFromUrl(): Payment.Status[] {
    const statusParam = this.route.snapshot.queryParamMap.get('status');
    if (!statusParam) return [];

    const validStatuses = Object.values(Payment.Status) as string[];
    return statusParam.split(',').filter((status): status is Payment.Status => validStatuses.includes(status));
  }

  private async restoreFiltersFromUrl() {
    const params = this.route.snapshot.queryParamMap;
    const customerName = params.get('customer');
    const distributorName = params.get('distributor');

    if (!customerName && !distributorName) return;

    const [selectedCustomer, selectedDistributor] = await Promise.all([
      customerName ? this.findCustomerByName(customerName) : Promise.resolve(null),
      distributorName ? this.findDistributorByName(distributorName) : Promise.resolve(null),
    ]);

    this.form.patchValue({
      ...(selectedCustomer ? { selectedCustomer } : {}),
      ...(selectedDistributor ? { selectedDistributor } : {}),
    });
  }

  private async findCustomerByName(name: string) {
    const result = await this.customerSvc.listCustomers({ search: name, page: 1, limit: 20 }).catch(() => null);
    return result?.items.find(customer => customer.name === name) ?? null;
  }

  private async findDistributorByName(name: string) {
    const globalDistributors = await firstValueFrom(this.securitySvc.globalDistributors$);
    const result = await this.securitySvc.listDistributorsWithPermissionKeys({
      search: name,
      permissionId: 'py_ViewSales',
      permissionKeys: globalDistributors?.map(dist => dist.permissionKey) ?? null,
    });

    return result.items.find(dist => dist.name === name) ?? null;
  }

  private syncFiltersToUrl() {
    const { selectedCustomer, selectedDistributor, status, warrantyId } = this.form.getRawValue();
    const nextParams = {
      customer: selectedCustomer?.name || null,
      customerId: null,
      status: status?.length ? status.join(',') : null,
      distributor: selectedDistributor?.name || null,
      distributorId: null,
      warrantyId: warrantyId || null,
    };

    const params = this.route.snapshot.queryParamMap;
    if (
      (params.get('customer') || null) === nextParams.customer &&
      !params.get('customerId') &&
      (params.get('status') || null) === nextParams.status &&
      (params.get('distributor') || null) === nextParams.distributor &&
      !params.get('distributorId') &&
      (params.get('warrantyId') || null) === nextParams.warrantyId
    ) {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: nextParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
